import { lazy, Suspense, useContext } from 'react';
import { Route, Routes, Navigate, Outlet, useLocation } from 'react-router-dom';
import { UserContext } from './context/UserContext';
import Loader from './components/Loader';
import Protocols from './pages/protocols/Protocols';
import AddEditProtocol from './pages/protocols/AddEditProtocol';
import Hospitals from './pages/Hospitals';
import Login from './pages/Login';
import Users from './pages/Users';
import AddUser from './pages/AddUser';
import EditUser from './pages/EditUser';
import ProtocolViewer from './pages/protocols/ProtocolViewer';
import ProtocolIdViewer from './pages/protocols/ProtocolIdViewer'; // Page for the "ID card"
import Welcome from './pages/Welcome';
import Training from './pages/Training';
import Settings from './pages/Settings';
import TestTubeManagement from './pages/test-tubes/TestTubes';
import AntibiogramConfig from './pages/apps-config-pages/antibiogram/AntibiogramConfig';
import ResusAdmin from './pages/resusAdmin/ResusAdmin';
import CasesList from './pages/MedicalCases/CasesList';
import MedicalCases from './pages/MedicalCases/MedicalCases';
import AddEditMedicalCase from './pages/MedicalCases/AddEditMedicalCase';
import CaseView from './pages/MedicalCases/CaseView';
import AppsMgmt from './pages/apps-mgmt/AppsMgmt';
import AddEditApp from './pages/apps-mgmt/AddEditApp';
import RegisterFromInvitation from './pages/RegisterFromInvitation';
import Onboarding from './pages/onboarding/Onboarding';
import Apps from './apps/Apps';
import PreemieNutritionConfig from './pages/apps-config-pages/preemie-nutrition/PreemieNutritionConfig';
import NicuNutritionConfig from './pages/apps-config-pages/nicu-nutrition/NicuNutritionConfig';
import NewPreemieConfig from './pages/apps-config-pages/new-preemie/NewPreemieConfig';
import SepsisAppConfig from './pages/apps-config-pages/sepsis-screening/SepsisAppConfig';
import HematologicalDiseasesConfig from './pages/apps-config-pages/hematological-diseases/HematologicalDiseasesConfig';
import PhototherapyConfig from './pages/apps-config-pages/phototherapy/PhototherapyConfig';
import Bili2022Config from './pages/apps-config-pages/bili2022/Bili2022Config';
import ResetPassword from './pages/ResetPassword';
import ResetPasswordConfirm from './pages/ResetPasswordConfirm';
import Checklists from './pages/checklists/Checklists';
import AddEditChecklistTemplate from './pages/checklists/AddEditChecklistTemplate';
import ChecklistInstance from './pages/checklists/ChecklistInstance';
import ChecklistTemplateView from './pages/checklists/ChecklistTemplateView';
import DepartmentDocuments from './pages/department-documents/DepartmentDocuments';
import DepartmentDocumentViewer from './pages/department-documents/DepartmentDocumentViewer';
import Homepage from './pages/homepage/Homepage';
import Stats from './pages/stats/Stats';
import EntraCallback from './pages/EntraCallback';
import Signin from './pages/Signin';
import InvitationSignup from './pages/InvitationSignup';
import { returnPathFromLocation, safeReturnPath } from './auth/returnPath';
import { isLegacyLoginClosed } from './auth/capabilityPolicy';

// Loaded on demand: it bundles the full generated medications JSON (~120 KB),
// which no other page needs.
const MedicationsAdmin = lazy(() => import('./pages/resusAdmin/v2/MedicationsAdmin'));

const ProtectedRoute = () => {
  const { authToken, isInitialized } = useContext(UserContext);
  const location = useLocation();

  if (!authToken) {
    return <Navigate replace to="/login" state={{ from: location }} />;
  }

  if (!isInitialized) {
    return (
      <div style={{ position: 'relative', minHeight: '100dvh' }}>
        <Loader isLoading={true} />
      </div>
    );
  }

  return <Outlet />;
};

const AppRoutes = () => {
  const { user, isInitialized, authToken, authMethod, authCapabilities } = useContext(UserContext);
  const location = useLocation(); // Add useLocation here
  // From the cutoff the legacy-only pages (the old signup wizard, password reset)
  // lead to the new /login (docs/EntraFullMigrationPlan.md §3.2).
  const legacyClosed = isLegacyLoginClosed(authCapabilities);
  const toLogin = <Navigate replace to="/login" />;

  const RootRedirect = () => {
    if (!authToken) {
      // Pass location state here as well
      return <Navigate replace to="/login" state={{ from: location }} />;
    }

    if (!isInitialized) {
      return (
        <div style={{ position: 'relative', minHeight: '100dvh' }}>
          <Loader isLoading={true} />
        </div>
      );
    }

    return (
      <Navigate replace to="/homepage" />
    );
  };

  const hospitalRoutes = [
    'kaplan',
    'emek',
    'assuta',
    'rambam',
    'ichilov',
    'szmc',
    'apps'
  ];

  if (!authToken) {
    // And pass location state here for the wildcard route
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signin" element={<Signin />} />
        {/* Sign-out used to return here. A client still running that code is sent
            on to /signin; remove once every client signs out to /signin. */}
        <Route path="/signed-out" element={<Navigate replace to="/signin" />} />
        <Route path="/auth/callback" element={<EntraCallback />} />
        {/* From the cutoff the invitation link goes straight to Microsoft's sign-up. */}
        <Route path="/register" element={legacyClosed ? <InvitationSignup /> : <RegisterFromInvitation />} />
        <Route path="/reset-password" element={legacyClosed ? toLogin : <ResetPassword />} />
        <Route path="/reset-password/confirm" element={legacyClosed ? toLogin : <ResetPasswordConfirm />} />
        <Route path="/onboarding/*" element={legacyClosed ? toLogin : <Onboarding />} />
        {/* Only a specific app is public. The hospital landing page (the app list at
            /main/:hospitalName) is deliberately absent here, so it falls through to
            the "*" route below and requires a login. */}
        <Route path="/main/:hospitalName/:appName" element={<Apps />} />
        {hospitalRoutes.map((hospital) => (
          <Route
            key={hospital}
            path={`/${hospital}/*`}
            element={
              <Navigate replace to={`/main${location.pathname}`}
              />
            }
          />
        ))}

        <Route
          path="*"
          element={<Navigate replace to="/login" state={{ from: location }} />}
        />
      </Routes>
    );
  }

  if (!isInitialized) {
    return (
      <div style={{ position: 'relative', minHeight: '100dvh' }}>
        <Loader isLoading={true} />
      </div>
    );
  }

  const entraOnboardingStatus = user?.entraOnboarding?.status;
  if (authMethod === 'entra' && location.pathname !== '/auth/callback') {
    if (
      ['profile', 'workplace'].includes(entraOnboardingStatus)
      && location.pathname !== '/onboarding/register'
      && !(location.pathname === '/onboarding/thank-you' && location.state?.outcome)
    ) {
      return <Navigate replace to="/onboarding/register" />;
    }
    // A pending join request is finished onboarding too, as in the legacy signup:
    // the user enters the app and /welcome says what awaits approval.
    if (
      ['completed', 'pending_approval'].includes(entraOnboardingStatus)
      && location.pathname === '/onboarding/register'
    ) {
      const returnTo = safeReturnPath(new URLSearchParams(location.search).get('returnTo'));
      return <Navigate replace to={returnTo || '/homepage'} />;
    }
  }

  return (
    <Routes>
      {/* Already signed in: no sign-in page, on to where the user was going. */}
      <Route path="/login" element={<Navigate replace to={returnPathFromLocation(location.state?.from) || '/homepage'} />} />
      <Route path="/register" element={<Navigate replace to="/homepage" />} />
      {/* A legacy session still reaches Microsoft from /signin (the way to the
          new sign-in); a Microsoft session goes in. */}
      <Route path="/signin" element={authMethod === 'legacy' ? <Signin /> : <RootRedirect />} />
      <Route path="/signed-out" element={<RootRedirect />} />
      <Route path="/auth/callback" element={<EntraCallback />} />
      <Route path="/reset-password" element={legacyClosed ? toLogin : <ResetPassword />} />
      {/* The emailed reset link is often opened on a device that is already signed
          in (e.g. a phone). Without this route it fell through to "*" and the
          reset form never showed. */}
      <Route path="/reset-password/confirm" element={legacyClosed ? toLogin : <ResetPasswordConfirm />} />
      <Route path="/main/:hospitalName/:appName" element={<Apps />} />
      <Route path="/main/:hospitalName" element={<Apps />} />
      {hospitalRoutes.map((hospital) => (
        <Route
          key={hospital}
          path={`/${hospital}/*`}
          element={
            <Navigate
              replace
              to={`/main${location.pathname}`}
            />
          }
        />
      ))}

      {/* Protected Routes */}
      <Route element={<ProtectedRoute />}>
        <Route path="/homepage" element={<Homepage />} />
        <Route path="/onboarding/*" element={<Onboarding />} />
        <Route path="/welcome" element={<Welcome />} />

        <Route path="/Emekid" element={<Navigate replace to="/protocols" />} />
        <Route path="/protocols" element={<Protocols />} />
        <Route path="/protocols/new" element={<AddEditProtocol />} />
        <Route path="/protocols/:id/view" element={<ProtocolViewer />} />
        <Route path="/protocols/:id" element={<ProtocolIdViewer />} />
        <Route path="/protocols/:id/edit" element={<AddEditProtocol />} />

        <Route path="/hospitals" element={<Hospitals />} />
        <Route path="/antibiogram" element={<AntibiogramConfig />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/test-tubes" element={<TestTubeManagement />} />
        <Route path="/resusAdmin" element={<ResusAdmin />} />
        <Route
          path="/resusAdmin/medications"
          element={
            <Suspense fallback={<Loader isLoading={true} />}>
              <MedicationsAdmin />
            </Suspense>
          }
        />
        <Route path="/preemie-nutrition-config" element={<PreemieNutritionConfig />} />
        <Route path="/nicu-nutrition-config" element={<NicuNutritionConfig />} />
        <Route path="/new-preemie-config" element={<NewPreemieConfig />} />
        <Route path="/sepsis-screening-config" element={<SepsisAppConfig />} />
        <Route path="/hematological-diseases-config" element={<HematologicalDiseasesConfig />} />
        <Route path="/phototherapy-config" element={<PhototherapyConfig />} />
        <Route path="/bili2022-config" element={<Bili2022Config />} />
        <Route path="/profile" element={<EditUser />} />

        <Route path="/users" element={<Users />} />
        <Route path="/users/new" element={<AddUser />} />
        <Route path="/users/:id" element={<EditUser />} />

        <Route path="/training" element={<Training />} />

        <Route path="/cases" element={<MedicalCases />}>
          <Route index element={<CasesList />} />
          <Route path=":id/view" element={<CaseView />} />
          <Route path=":id/edit" element={<AddEditMedicalCase />} />
          <Route path="new" element={<AddEditMedicalCase />} />
        </Route>

        <Route path="/checklists" element={<Checklists />} />
        <Route path="/checklists/templates/new" element={<AddEditChecklistTemplate />} />
        <Route path="/checklists/templates/view/:templateId" element={<ChecklistTemplateView />} />
        <Route path="/checklists/templates/edit/:id" element={<AddEditChecklistTemplate />} />
        <Route path="/checklists/instance/:instanceId" element={<ChecklistInstance />} />

        <Route path="/department-documents" element={<DepartmentDocuments />} />
        <Route path="/department-documents/:id" element={<DepartmentDocumentViewer />} />

        <Route path="/apps-mgmt" element={<AppsMgmt />} />
        <Route path="/apps-mgmt/add" element={<AddEditApp />} />
        <Route path="/apps-mgmt/edit/:id" element={<AddEditApp />} />

        <Route path="/stats" element={<Stats />} />
      </Route>

      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<Navigate to="/protocols" />} />
    </Routes>
  );
};

export default AppRoutes;

